import React, { Fragment, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { Price } from '@thd-olt-component-react/price';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { KeyProductFeatures } from '@thd-olt-component-react/key-product-features';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  string,
  bool,
  func,
  arrayOf,
  shape
} from 'prop-types';
import {
  ProductPod as ProductPodOlt,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductIdentifier,
  ProductImage,
  ProductCompare,
  ProductSponsored
} from '@thd-olt-component-react/product-pod';
import { Promotion } from '@thd-olt-component-react/promotion';
import { RecommendedAcc } from './RecommendedAcc/RecommendedAcc';
import { useCabinetVisualizerStore } from '../../../store';
import { AccessoryTypes } from './RecommendedAcc/types';
import { WallPriceProduct } from './WallPriceProduct';
import {
  selectDesignName,
  selectDesignById,
  selectProductsModalType,
  selectDesignProducts
} from '../../../store/cabinetVisualizer/selectors';
import {
  editProduct, removeProduct,
} from '../CabinetVisualizer/utils';
import { EMPTY_OBJECT } from '../../../utils/empty-reference';
import { useCartRemoveAccessories } from '../CabinetVisualizer/hooks/cabinetVisualizerHooks';
import { ConfirmationModal } from './ConfirmationModal';

const cabinetTypes = ['Base', 'Wall'];

export const WallPriceSummary = ({
  id,
  expanded,
  products,
  onChange,
  handleQuickView,
  isMobile
}) => {
  const {
    updateDesignProducts,
    cabinetVisualizer,
  } = useCabinetVisualizerStore();

  const { itemPrices } = cabinetVisualizer;

  const name = selectDesignName(cabinetVisualizer, id);
  const host = useConfigService('global-custom-commerce:host');
  const metadataBaseUrl = useConfigService('graphQL:metadataBaseUrl');
  const { ProductParentIds } = selectDesignById(cabinetVisualizer, id) || EMPTY_OBJECT;
  const removeAccessoryFromCart = useCartRemoveAccessories(id);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  // const isLoading = allProducts.length === 0;
  const groupedProductsArray = useMemo(() => {
    return products.reduce((acc, product) => {
      const itemId = product.itemId;
      if (!itemId || itemId < 0) return acc;

      const existingProduct = acc.find((pr) => pr.itemId === itemId);
      if (existingProduct) {
        existingProduct.quantity += product.quantity;
      } else {
        const modalType = selectProductsModalType(cabinetVisualizer, itemId, id);
        acc.push({
          itemId,
          quantity: product.quantity,
          type: cabinetTypes.includes(modalType) ? 'Cabinets' : modalType,
        });
      }

      return acc; // This is needed to return the accumulator in reduce
    }, []);
  }, [products]);

  const totalCabinets = groupedProductsArray.reduce(
    (acc, product) => (product.type === 'Cabinets' ? acc + product.quantity : acc),
    0
  );

  const totalAccessories = groupedProductsArray.reduce(
    (acc, product) => (product.type !== 'Cabinets' ? acc + product.quantity : acc),
    0
  );

  const handleRemoveClick = (itemId, itemName, quantity) => {
    setItemToRemove({ itemId, itemName, quantity });
    setModalOpen(true);
  };

  const handleConfirmRemove = () => {

    if (itemToRemove) {

      if (itemToRemove.itemName.toLowerCase() === 'filler') {
        const existingProdutcs = selectDesignProducts(cabinetVisualizer, id);
        const existingProductIndex = existingProdutcs.findIndex((acc) => acc.Sku === itemToRemove.itemId);
        const parentId = ProductParentIds ? ProductParentIds[`${itemToRemove.itemId}`] : '';
        let existingProductObject = null;
        if (existingProductIndex !== -1) {
          existingProductObject = existingProdutcs[existingProductIndex];
          updateDesignProducts(
            host,
            id,
            removeProduct(cabinetVisualizer, id, itemToRemove.itemId, parentId, existingProductObject?.Origin),
            { parentId, itemId: itemToRemove.itemId }, metadataBaseUrl);

        }

      }
      removeAccessoryFromCart(itemToRemove.itemId);
      setItemToRemove(null);
      setModalOpen(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setItemToRemove(null); // Clear the item to remove when closing
  };

  let priceDisplay = '$0.00';
  if (groupedProductsArray.length > 0) {
    priceDisplay = `$${groupedProductsArray.reduce((acc, product) => {
      const itemPrice = itemPrices.find((price) => product.itemId === price.itemId)?.price || 0;
      return acc + (itemPrice * product.quantity);
    }, 0).toFixed(2)}`;
  }

  return (
    <div>
      <Accordion expanded={expanded} onChange={onChange} noBorders>
        <AccordionHeader>
          <div className="sui-flex-auto sui-block">
            <div>
              <Typography variant="h5" weight="regular">{name}</Typography>
            </div>
            <div className="sui-flex sui-justify-between">
              <div>
                <Typography
                  variant="body-small"
                  style={{ color: '#5E5E5E' }}
                >
                  {`${totalCabinets} Cabinets & `}
                  {`${totalAccessories} Accessor${totalAccessories === 1 ? 'y' : 'ies'}`}
                </Typography>
              </div>
              <div className="price-container sui-flex">
                <span>
                  <Typography variant="body-small" style={{ color: '#5E5E5E' }}>
                    {priceDisplay}
                  </Typography>
                </span>
              </div>
            </div>
          </div>
        </AccordionHeader>
        <AccordionBody>
          <div className="sui-flex sui-justify-between sui-items-start">
            <div className="sui-flex sui-grow">
              <div className="sui-flex-auto sui-block">
                {AccessoryTypes.map((type) => (
                  <Fragment key={type.id}>
                    <RecommendedAcc
                      accessory={type}
                      handleQuickView={handleQuickView}
                      designId={id}
                      isMobile={isMobile}
                    />
                    {groupedProductsArray.filter((product) => product.type === type.type).map((product) => (
                      <Fragment key={product.itemId}>
                        <WallPriceProduct
                          itemId={product.itemId}
                          quantity={product.quantity}
                          type={product.type}
                          handleRemove={() => handleRemoveClick(product.itemId,
                            product.type, product.quantity)} // Pass item details
                        />
                        <ConfirmationModal
                          isOpen={modalOpen && itemToRemove?.itemId === product.itemId} // Open only for this product
                          onClose={handleCloseModal}
                          onConfirm={handleConfirmRemove}
                          itemName={product.type}
                          headerTitle={`Remove ${product.type}`} // Use product type as item name
                          quantity={product.quantity}
                          message={type.removeMessage}
                          confirmButtonName="Remove"
                        />
                      </Fragment>
                    ))}
                  </Fragment>
                ))}
              </div>
            </div>
            <div className="sui-flex sui-items-start sui-ml-2" />
            <div className="sui-transition-transform sui-duration-250 sui-h-4 sui-flex sui-items-start sui-rotate-180">
              <svg
                className="sui-inline-block sui-align-baseline sui-fill-inherit sui-w-4 sui-h-4"
                viewBox="0 0 24 24"
                focusable="false"
                aria-hidden="true"
                data-testid="ArrowDownIcon"
                style={{ visibility: 'hidden' }}
              >
                <path d="M23.979 6.862 12 18.841.021 6.863l1.591-1.591L12 15.659 22.387 5.271l1.591 1.591Z" />
              </svg>
            </div>
          </div>
        </AccordionBody>
      </Accordion>
    </div>
  );
};

WallPriceSummary.propTypes = {
  id: string.isRequired,
  products: arrayOf(shape()),
  expanded: bool,
  onChange: func,
  handleQuickView: func.isRequired,
  isMobile: bool
};

WallPriceSummary.dataModel = extend(
  WallPriceProduct,
  ProductPodOlt,
  AddToList,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  KeyProductFeatures,
  Price,
  ProductATC,
  ProductCompare,
  ProductHeader,
  ProductIdentifier,
  ProductImage,
  ProductQuantity,
  ProductRating,
  ProductSample,
  ProductSponsored,
  Promotion
);

WallPriceSummary.defaultProps = {
  expanded: false,
  products: [],
  onChange: () => {},
  isMobile: false
};
